import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import useStyles from '../../../assets/styles/DQProV2PlansStyles';
import PoweredByDQ_Default from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import { getLearningProgramPlansService } from '../../../services/learning_programs.service';
import { selectClientPlanService } from '../../../services/client.service';
import { refreshToken } from '../../../services/auth.service';
import { toast, toastWarning } from '../../../utils/utils';
import { TOAST_TYPES } from '../../../utils/constants';
import { useAuth } from '../../../context/auth';
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";

const DQProV2Plans = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const { setAuthToken, setAuthUser } = useAuth();
    const history = useHistory();
    const [plansData, setPlansData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selecting, setSelecting] = useState(null);
    const [currentPlanKey, setCurrentPlanKey] = useState(null);

    useEffect(() => {
        loadPlans();
    }, []);

    const loadPlans = async () => {
        setLoading(true);
        try {
            const res = await getLearningProgramPlansService();
            const plans = res?.data?.plans || [];
            const currentPlan = res?.data?.current_plan || null;
            setPlansData(plans);
            setCurrentPlanKey(currentPlan?.key || null);
        } catch (e) {
            toastWarning('Failed to load plans');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectPlan = async (planKey) => {
        if (!planKey || selecting) return;
        setSelecting(planKey);
        let planActivated = false;
        try {
            const response = await selectClientPlanService(planKey);
            if (response?.data?.success) {
                planActivated = true;
                setCurrentPlanKey(planKey);
                toast(TOAST_TYPES.SUCCESS, 'Plan Selected', `Plan "${planKey}" selected successfully!`);
                // Refresh AuthContext so access-level-gated views update immediately
                try {
                    const infoRes = await refreshToken();
                    if (infoRes?.data?.auth_token) setAuthToken(infoRes.data.auth_token);
                    if (infoRes?.data?.user) setAuthUser(infoRes.data.user);
                } catch (_) {}
            }
        } catch (e) {
            toastWarning('Failed to select plan. Please try again.');
        } finally {
            setSelecting(null);
            if (planActivated) history.push('/my_workspace');
        }
    };

    return (
        <Box className={classes.pagePage}>
            {/* Custom Header */}
            <Box className={classes.header}>
                <Box>
                    <img src={PoweredByDQ_Logo} alt="Logo" className={classes.logo} />
                </Box>
                <Box>
                    <Button className={classes.upgradeBtn}>Upgrade</Button>
                </Box>
            </Box>

            {/* Content Area */}
            <Box className={classes.contentArea}>
                <Typography className={classes.title}>Choose the plan that's right for you</Typography>
                <Typography className={classes.subtitle}>Choose and manage your Powered by DQ plan.</Typography>
                
                <Box className={classes.cardsContainer}>
                    {plansData.map((plan, index) => (
                        <Box key={plan.key || index} className={`${classes.card} ${plan.recommended ? classes.recommendedCard : ''}`}>
                            {plan.recommended && <Box className={classes.badge}>Recommended</Box>}
                            
                            <Box className={classes.planTitleWrapper}>
                                <Typography className={classes.planTitle}>{plan.label}</Typography>
                            </Box>
                            
                            <Box className={classes.priceWrapper}>
                                <Typography className={classes.price}>${plan.price_monthly_usd}</Typography>
                                <Typography className={classes.period}>/mo</Typography>
                            </Box>

                            <Box className={classes.divider} />

                            <Box className={classes.featureTitleWrapper}>
                                <Typography className={classes.featureTitle}>{plan.label}</Typography>
                            </Box>
                            
                            <Box className={classes.descriptionWrapper}>
                                <Typography className={classes.description}>{plan.description}</Typography>
                            </Box>
                            
                            {/* # = bold section header, - = bullet item */}
                            <div className={classes.featuresList}>
                                {(plan.features || []).map((item, idx) => {
                                    if (item.startsWith('# ')) {
                                        return (
                                            <div key={idx} className={classes.featureSectionHeader}>
                                                {item.slice(2)}
                                            </div>
                                        );
                                    }
                                    const text = item.startsWith('- ') ? item.slice(2) : item;
                                    return (
                                        <div key={idx} className={classes.featureItem}>
                                            <span>•</span>
                                            <span>{text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <Typography className={classes.extraInfo}>+${plan.student_license_usd}/student annual license</Typography>

                            <Button
                                className={classes.actionButton}
                                disabled={!!selecting || currentPlanKey === plan.key}
                                onClick={() => handleSelectPlan(plan.key)}
                            >
                                {selecting === plan.key
                                    ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                                    : currentPlanKey === plan.key
                                        ? 'Current Plan'
                                        : plan.cta_label}
                            </Button>
                        </Box>
                    ))}
                </Box>
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </Box>
    );
};

export default DQProV2Plans;
